import React, { useRef, useState } from 'react';
import CompGridMatchResult from '@components/grid/comp-grid-matchResult.js';
import CompFormMatchResult from '@components/form/comp-form-matchResult.js';
import CompFormMatchResultAward from '@components/form/comp-form-matchResult-award.js';
import SearchForm from '@components/form/fragment/comp-fmt-search.js';
import ButtonForm from '@components/form/fragment/comp-fmt-button.js';
import { base64ToBlob, downloadCurrentPdf, isEmpty } from '@assets/js/util/commonUtil.js';
import { removeGrid } from '@assets/js/api/commonApi.js';
import { ApiDownload } from '@assets/js/api/commonApi.js';

// 로딩 스피너 컴포넌트
const LoadingOverlay = ({ message }) => (
    <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
    }}>
        <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ color: 'white', marginTop: '1rem', fontSize: '1.1rem' }}>{message}</p>
    </div>
);

const MatchResultMng = (props) => {
    /****************************
     * Define required variables
     *****************************/
    const childRef = useRef();
    const childGridRef = useRef();
    const childAwardRef = useRef();
    const [params, setParams] = useState({});
    const [selectedRows, setSelectedRows] = useState({ ids: [], data: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    /**************************
     * Define button functions
     ***************************/
    const onNew = () => {
        childRef.current.modalToggle({mode: 'I'});
    }
    const onModify = (params) => {
        childRef.current.modalToggle({mode: 'M', ...params});
    }
    const onRemove = () => {
        if (selectedRows.length === 0) { alert('1건이상 선택해주세요.'); return; }
        const idsArray = [...selectedRows.ids];
        if (idsArray.length === 0) {
            alert('삭제할 항목을 선택해주세요.');
            return;
        }

        const deleteParams = {
            deleteList: idsArray.map(rowId => {
                // rowId format: mngCd|mtmkgCd|armType|plyrCd
                const [mngCd, mtmkgCd, armType, plyrCd] = rowId.split('|');
                return {
                    mngCd: mngCd,
                    mtmkgCd: mtmkgCd,
                    armType: armType,
                    plyrCd: plyrCd
                };
            })
        };

        removeGrid('/sys/matchresultmng/remove/info', deleteParams, {
            msg: {
                confirm: '선택된 ' + idsArray.length + '개 항목을 삭제하시겠습니까?',
            }
        }).then(res => {
            if (res && res.success) {
                //clear selected rows grid.
                setSelectedRows(new Set());
                onGridSearch();
            }
        });
    }

    //대회 상장정보 등록.
    const onAwardCertificateSave = () => {
        childAwardRef.current.modalToggle(selectedRows.data || {});
    }
    //대회 상장 PDF 다운로드.
    const onAwardCertificateDown = () => {
        if (selectedRows.length === 0) { alert('1건이상 선택해주세요.'); return; }
        const idsArray = [...selectedRows.ids];
        if (idsArray.length === 0) {
            alert('다운로드할 항목을 선택해주세요.');
            return;
        }

        const dataChkMap = selectedRows.data[0];
        if (isEmpty(dataChkMap.awardUserNm) || isEmpty(dataChkMap.awardUserPsNm)|| isEmpty(dataChkMap.awardComptnDt)  || isEmpty(dataChkMap.awardContentTxt)) {
            alert("상장 출력에 필요한 '상장 정보 등록'을 해주세요.");
            return;
        }

        const searchParams = {
            trmntResultList: selectedRows.data.map(item => {
                return item;
            })
        }

        // 로딩 표시 시작
        setIsLoading(true);
        setLoadingMessage(`PDF 생성 중... (${selectedRows.data.length}건)`);

        ApiDownload('/sys/matchresultmng/award/print', searchParams, {
            isMsgShow: false,
            timeout: 300000 // 5분 (300,000ms) 타임아웃 설정
        }).then(res => {
            if (res && res.success) {
                setLoadingMessage('PDF 다운로드 중...');

                selectedRows.data.map((item, index) => {
                    setTimeout(() => {
                        //pdf render.
                        const blob = base64ToBlob(res.data.data[`${item.plyrCd}_jBytes`], 'application/pdf');
                        //pdf download.
                        downloadCurrentPdf(blob, `CompetitionAward_${item.plyrCd}`);

                        // 마지막 항목이면 로딩 종료
                        if (index === selectedRows.data.length - 1) {
                            setTimeout(() => {
                                setIsLoading(false);
                                setLoadingMessage('');
                            }, 500);
                        }
                    }, index * 300); // 각 다운로드 사이에 300ms 간격
                });
            } else {
                setIsLoading(false);
                setLoadingMessage('');
                alert('PDF 생성에 실패했습니다.');
            }
        }).catch(error => {
            setIsLoading(false);
            setLoadingMessage('');
            alert('PDF 생성 중 오류가 발생했습니다.');
            console.error('PDF generation error:', error);
        });
    }
    
    const onGridSearch = () => {
        childGridRef.current.onGridSearch(params);
    }
    const onValChange = (e) => {
        const { id, value } = e.target;
        // Update the state with the new value
        setParams({ ...params, [id]: value });
    }

    /****************************
     * Define form config
     *****************************/
    const formObj = {
        id : 'searchForm',
        params : {
            comptnNm : {
                id: 'comptnNm',
                label: '대회',
                maxLength : 100,
            },
            plyrNm : {
                id : 'plyrNm',
                label : '선수명',
                maxLength : 100,
            },
            rnk : {
                id : 'rnk',
                label : '등수',
                maxLength : 10,
                //newLine : true
            },
        }
    }
    const buttonConfig = {
        fncButtons: [
            { id: 'awardCertificateSave', label: '상장 정보 등록', className: 'btn-success', onClick: onAwardCertificateSave },
            { id: 'awardCertificateDown', label: '상장 PDF', className: 'btn-primary', onClick: onAwardCertificateDown },
        ],
        leftButtons: [
            { id: 'new', label: 'New', className: 'btn-success', onClick: onNew },
            { id: 'remove', label: 'Remove', className: 'btn-danger', onClick: onRemove }
        ],
        rightButtons: [
            { id: 'search', label: 'Search', className: 'btn-primary', onClick: onGridSearch }
        ]
    }

    return (
        <>
            {/* 로딩 오버레이 */}
            {isLoading && <LoadingOverlay message={loadingMessage} />}

            {/* SearchForm */}
            <SearchForm formObj={formObj} onValChange={onValChange} onGridSearch={onGridSearch} params={params}/>

            {/* ButtonForm */}
            <ButtonForm buttonConfig={buttonConfig} />

            {/* Grid */}
            <CompGridMatchResult ref={childGridRef} onChildClick={onModify} onSelectionChange={setSelectedRows} />

            {/* Modal popup */}
            <CompFormMatchResult ref={childRef} onGridSearch={onGridSearch} />

            {/* Award Certificate data save Modal popup */}
            <CompFormMatchResultAward ref={childAwardRef} onGridSearch={onGridSearch} />
        </>
    );
}

export default MatchResultMng;